import { type InvoicingOverviewDto, type MeDto, type PurchaseFilter, type ReceivableDto, datePl, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Card, Group, Select, SimpleGrid, Stack, Table, Tabs, Text, Title } from '@mantine/core';
import { IconCash, IconFilter } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../api';
import { can } from '../auth';
import { CompanyBadgeById, PaymentStatusBadge } from '../components/Badges';
import { ScheduleDocCell } from '../components/InvoicesPanel';
import { Money } from '../components/Money';
import { PageHeader } from '../components/PageHeader';
import { PaymentDialog } from '../components/PaymentDialog';
import { LastReminder, PaymentClaimNote, PaymentReminderButton } from '../components/PaymentReminder';
import { QueryState } from '../components/QueryState';
import { EmptyState, FilterChips } from '../components/ui';
import { useCompanies } from '../data';
import { FinanceInvoicing } from './FinanceInvoicing';
import { FinanceDashboard } from './finance/FinanceDashboard';
import { Purchases } from './finance/Purchases';
import { SalesRegister } from './finance/SalesRegister';
import { ServiceToInvoice } from './finance/ServiceToInvoice';

/** Zakładki Finansów (?widok=…); „faktury” = do zafakturowania (dotychczasowe linki). */
const VIEWS = ['pulpit', 'naleznosci', 'faktury', 'sprzedaz', 'zakupy'] as const;
type View = (typeof VIEWS)[number];

const remaining = (r: ReceivableDto) => Math.max(r.amountGr - r.paidGr, 0);
const isOpen = (r: ReceivableDto) => r.status === 'oczekuje' || r.status === 'czesciowo';

/** Liczba dni od terminu do dziś (daty RRRR-MM-DD, bez wpływu zmiany czasu). */
function daysAfter(dueDate: string, today: string): number {
  return Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${dueDate}T12:00:00Z`)) / 86_400_000);
}
const daysLabel = (n: number) => `${n} ${n === 1 ? 'dzień' : 'dni'}`;

/**
 * Finanse — narzędzie księgowej: pulpit, należności z harmonogramów umów, dokumenty do wystawienia,
 * rejestr faktur sprzedaży (Fakturownia) i faktury zakupu pobierane z KSeF.
 */
export function FinancePage({ me }: { me: MeDto }) {
  const qc = useQueryClient();
  const companies = useCompanies();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [mode, setMode] = useState('open');
  const [paying, setPaying] = useState<ReceivableDto | null>(null);
  const canPay = can(me, 'finanse.wplaty');
  // dokumenty do pozycji (pro forma do zaliczki, faktury) — te same działania co w harmonogramie umowy
  const showDocs = can(me, 'faktury.podglad');
  const canSales = can(me, 'faktury.podglad');
  const canPurchases = can(me, 'zakupy.podglad');
  // magazynier: same faktury zakupu (przyjęcia do magazynu) — bez pulpitu i należności
  const canFinance = can(me, 'finanse.podglad');
  const allowed = (v: View) => (v === 'sprzedaz' ? canSales : v === 'faktury' ? canSales && canFinance : v === 'zakupy' ? canPurchases : canFinance);
  // ?widok=… — zakładka (linki z pulpitu, powiadomień, kafelków); ?filtr=… — filtr zakupów
  const [searchParams, setSearchParams] = useSearchParams();
  const asked = searchParams.get('widok') as View | null;
  const view: View = asked && VIEWS.includes(asked) && allowed(asked) ? asked : canFinance || !canPurchases ? 'pulpit' : 'zakupy';
  const purchaseFilter = (searchParams.get('filtr') as PurchaseFilter | null) ?? undefined;
  // ?sprawa=numer — z zadania „Rozlicz…” / „Co teraz?” w sprawie: tylko pozycje tej sprawy
  const caseFilter = searchParams.get('sprawa');
  const clearCase = () =>
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('sprawa');
        return n;
      },
      { replace: true },
    );
  const inCase = <T extends { caseNumber: string }>(rows: T[]) => (caseFilter ? rows.filter((r) => r.caseNumber === caseFilter) : rows);
  const setView = (v: string | null) =>
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('filtr');
        if (v && v !== 'pulpit') n.set('widok', v);
        else n.delete('widok');
        return n;
      },
      { replace: true },
    );
  const invoicing = useQuery({ queryKey: ['invoicing-overview'], queryFn: () => api<InvoicingOverviewDto>('/finance/invoicing-overview'), enabled: canFinance });
  const lateInvoicing = invoicing.data ? invoicing.data.uninvoicedPayments.filter((r) => r.overdue).length + invoicing.data.unpaidInvoices.filter((r) => r.overdue).length : 0;
  const today = todayInWarsaw();
  const params = new URLSearchParams();
  if (companyId) params.set('companyId', companyId);
  if (mode === 'open') params.set('open', 'true');
  if (mode === 'overdue') params.set('overdue', 'true');
  const q = useQuery({ queryKey: ['receivables', params.toString()], queryFn: () => api<ReceivableDto[]>(`/finance/receivables?${params}`), enabled: canFinance });
  // Podsumowanie firm liczone zawsze ze wszystkich otwartych należności — niezależnie od zakładki i filtra firmy.
  const open = useQuery({ queryKey: ['receivables', 'open=true'], queryFn: () => api<ReceivableDto[]>('/finance/receivables?open=true'), enabled: canFinance });
  const byCompany = (companies.data ?? []).map((c) => {
    const rows = (open.data ?? []).filter((r) => r.companyId === c.id && isOpen(r));
    // QA P27: zaległości z umów jeszcze niepodpisanych liczone osobno
    const overdue = rows.filter((r) => r.overdue && r.contractSigned);
    const unsigned = rows.filter((r) => r.overdue && !r.contractSigned);
    return {
      c,
      due: rows.reduce((a, r) => a + remaining(r), 0),
      dueCount: rows.length,
      overdue: overdue.reduce((a, r) => a + remaining(r), 0),
      overdueCount: overdue.length,
      unsigned: unsigned.reduce((a, r) => a + remaining(r), 0),
      unsignedCount: unsigned.length,
    };
  });
  // Pozycje do wyboru w oknie wpłaty: wszystkie otwarte pozycje tej samej umowy (także spoza bieżącego widoku).
  const payItems = paying
    ? [paying, ...(open.data ?? []).filter((r) => r.contractId === paying.contractId && r.itemId !== paying.itemId)].map((r) => ({ id: r.itemId, name: r.name, amountGr: r.amountGr, paidGr: r.paidGr, status: r.status }))
    : [];

  return (
    <>
      <PageHeader
        title="Finanse"
        description="Należności i wpłaty, faktury sprzedaży wystawiane przez Fakturownię (faktura do wpłaty najpóźniej 15. dnia następnego miesiąca) i faktury zakupu pobierane z KSeF — osobno dla każdej firmy."
      />
      {/* wybór firmy — wspólny dla wszystkich zakładek */}
      <Group mb="md">
        <Select placeholder="Wszystkie firmy" clearable data={(companies.data ?? []).map((c) => ({ value: c.id, label: c.shortName }))} value={companyId} onChange={setCompanyId} w={{ base: '100%', sm: 240 }} />
      </Group>
      <Tabs value={view} onChange={setView} keepMounted={false} mb="md">
        <Tabs.List>
          {canFinance && <Tabs.Tab value="pulpit">Pulpit</Tabs.Tab>}
          {canFinance && <Tabs.Tab value="naleznosci">Należności</Tabs.Tab>}
          {canSales && canFinance && (
          <Tabs.Tab
            value="faktury"
            rightSection={
              lateInvoicing > 0 ? (
                <Badge size="xs" color="red" variant="filled">
                  {lateInvoicing}
                </Badge>
              ) : undefined
            }
          >
            Do zafakturowania
          </Tabs.Tab>
          )}
          {canSales && <Tabs.Tab value="sprzedaz">Faktury sprzedaży</Tabs.Tab>}
          {canPurchases && <Tabs.Tab value="zakupy">Faktury zakupu</Tabs.Tab>}
        </Tabs.List>
      </Tabs>
      {view === 'pulpit' && <FinanceDashboard me={me} companyId={companyId} open={open} invoicing={invoicing} />}
      {view === 'sprzedaz' && <SalesRegister me={me} companyId={companyId} />}
      {view === 'zakupy' && <Purchases companyId={companyId} initialFilter={purchaseFilter} />}
      {caseFilter && (view === 'faktury' || view === 'naleznosci') && (
        <Alert color="blue" variant="light" mb="md" icon={<IconFilter size={16} />}>
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm">
              Pokazano tylko sprawę <b>{caseFilter}</b> — z zadania rozliczenia.
            </Text>
            <Button size="xs" variant="subtle" onClick={clearCase}>
              Pokaż wszystkie
            </Button>
          </Group>
        </Alert>
      )}
      {view === 'faktury' && (
        <>
          <FinanceInvoicing
            me={me}
            q={
              caseFilter && invoicing.data
                ? ({
                    ...invoicing,
                    data: {
                      ...invoicing.data,
                      uninvoicedPayments: inCase(invoicing.data.uninvoicedPayments),
                      proformaDue: inCase(invoicing.data.proformaDue),
                      finalDue: inCase(invoicing.data.finalDue),
                      unpaidInvoices: inCase(invoicing.data.unpaidInvoices),
                    },
                  } as typeof invoicing)
                : invoicing
            }
            companies={companies.data ?? []}
            companyId={companyId}
          />
          {can(me, 'faktury.wystawianie', 'W') && (
            <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>
              <ServiceToInvoice highlight={searchParams.get('serwis')} />
            </div>
          )}
        </>
      )}
      {view === 'naleznosci' && (
        <>
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        {byCompany.map(({ c, due, dueCount, overdue, overdueCount, unsigned, unsignedCount }) => (
          <Card key={c.id} withBorder style={{ borderLeft: `4px solid ${c.color}` }}>
            <Group justify="space-between" align="baseline" gap="xs">
              <Title order={6}>{c.shortName}</Title>
              <Text size="xs" c="dimmed">
                wszystkie otwarte należności, niezależnie od filtrów
              </Text>
            </Group>
            <Text size="sm">
              Do zapłaty: <b><Money gr={due} /></b>{' '}
              <Text span size="xs" c="dimmed">
                ({dueCount} poz.)
              </Text>
            </Text>
            <Text size="sm" c={overdue > 0 ? 'var(--app-text-red)' : 'dimmed'} fw={overdue > 0 ? 600 : 400}>
              Po terminie: <Money gr={overdue} />{' '}
              {overdueCount > 0 && (
                <Text span size="xs" c="var(--app-text-red)">
                  ({overdueCount} poz.)
                </Text>
              )}
            </Text>
            {unsignedCount > 0 && (
              <Text size="sm" c="dimmed">
                Po terminie, umowa niepodpisana: <Money gr={unsigned} />{' '}
                <Text span size="xs" c="dimmed">
                  ({unsignedCount} poz.)
                </Text>
              </Text>
            )}
          </Card>
        ))}
      </SimpleGrid>
      <Group mb="md">
        <FilterChips
          value={mode}
          onChange={setMode}
          options={[
            { value: 'open', label: 'Do zapłaty' },
            { value: 'overdue', label: 'Po terminie' },
            { value: 'all', label: 'Wszystkie' },
          ]}
        />
      </Group>
      <Card padding={0}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Table.ScrollContainer minWidth={showDocs ? 1080 : 900}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Umowa / sprawa</Table.Th>
                <Table.Th>Płatność</Table.Th>
                <Table.Th ta="right">Kwota</Table.Th>
                <Table.Th ta="right">Wpłacono</Table.Th>
                <Table.Th>Termin</Table.Th>
                <Table.Th>Stan</Table.Th>
                {showDocs && <Table.Th>Dokument</Table.Th>}
                {canPay && <Table.Th />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {inCase(q.data ?? []).map((r) => {
                const late = r.overdue && isOpen(r);
                // QA P4: termin obowiązujący — z wystawionej faktury / pro formy, bez niej termin pozycji
                const due = r.invoiceDue?.date ?? r.dueDate;
                return (
                  <Table.Tr key={r.itemId} style={late ? { backgroundColor: 'var(--mantine-color-red-light)' } : undefined}>
                    <Table.Td>
                      <Group gap={6}>
                        <CompanyBadgeById id={r.companyId} />
                        <Anchor component={Link} to={`/umowy/${r.contractId}`} size="sm">
                          {r.contractNumber}
                        </Anchor>
                        {!r.contractSigned && (
                          <Badge size="xs" variant="light" color="gray">
                            umowa niepodpisana
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        <Anchor component={Link} to={`/sprawy/${r.caseId}?zakladka=platnosci`} size="xs">
                          {r.caseNumber}
                        </Anchor>{' '}
                        · {r.clientName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {r.name}{' '}
                      {r.conditionsStart && (
                        <Badge size="xs" color="orange" variant="light">
                          warunek startu
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Money gr={r.amountGr} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <Money gr={r.paidGr} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={late ? 'var(--app-text-red)' : undefined} fw={late ? 600 : 400}>
                        {due ? datePl(due) : 'po zdarzeniu'}
                      </Text>
                      {r.invoiceDue && (
                        <Text size="xs" c="dimmed">
                          z faktury {r.invoiceDue.number ?? ''}
                          {r.dueDate && r.dueDate !== r.invoiceDue.date ? ` · harmonogram ${datePl(r.dueDate)}` : ''}
                        </Text>
                      )}
                      {late && due && (
                        <Text size="xs" c="var(--app-text-red)">
                          po terminie {daysLabel(daysAfter(due, today))}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <PaymentStatusBadge status={r.status} overdue={late} />
                      <LastReminder r={r.lastReminder} />
                      {/* QA qa10311123 3.4: klient zgłosił wpłatę — do sprawdzenia na wyciągu i zaksięgowania */}
                      <PaymentClaimNote claim={r.paymentClaim} />
                    </Table.Td>
                    {showDocs && (
                      <Table.Td>
                        <ScheduleDocCell contractId={r.contractId} paymentItemId={r.itemId} />
                      </Table.Td>
                    )}
                    {canPay && (
                      <Table.Td ta="right">
                        {isOpen(r) && (
                          <Stack gap={2} align="flex-end">
                            <Button size="compact-xs" variant="light" leftSection={<IconCash size={14} />} onClick={() => setPaying(r)}>
                              Zarejestruj wpłatę
                            </Button>
                            <PaymentReminderButton itemId={r.itemId} />
                          </Stack>
                        )}
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {!inCase(q.data ?? []).length && (
          <EmptyState
            compact
            title={mode === 'overdue' ? 'Brak należności po terminie' : mode === 'open' ? 'Brak należności do zapłaty' : 'Brak należności'}
            hint={companyId ? 'Dla wybranej firmy — zmień firmę albo wyczyść filtr.' : undefined}
          />
        )}
      </QueryState>
      </Card>
        </>
      )}
      {paying && (
        <PaymentDialog
          opened
          onClose={() => setPaying(null)}
          contractId={paying.contractId}
          items={payItems}
          initialItemId={paying.itemId}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['receivables'] });
            void qc.invalidateQueries({ queryKey: ['invoicing-overview'] });
            void qc.invalidateQueries({ queryKey: ['case-receivables', paying.caseId] });
            void qc.invalidateQueries({ queryKey: ['contract', paying.contractId] });
            void qc.invalidateQueries({ queryKey: ['contract-invoicing', paying.contractId] });
            setPaying(null);
          }}
        />
      )}
    </>
  );
}
